import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface Rule {
  key: string;
  test: (pw: string) => boolean;
}

const rules: Rule[] = [
  { key: 'rule8chars', test: (pw) => pw.length >= 8 },
  { key: 'ruleUppercase', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'ruleLowercase', test: (pw) => /[a-z]/.test(pw) },
  { key: 'ruleDigit', test: (pw) => /\d/.test(pw) },
  { key: 'ruleSpecial', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

export const validatePassword = (password: string): boolean =>
  rules.every((r) => r.test(password));

const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps) => {
  const { t } = useTranslation('account');
  const results = useMemo(() => rules.map((r) => ({ ...r, pass: r.test(password) })), [password]);
  const score = results.filter((r) => r.pass).length;

  const strengthLabel = score <= 1 ? t('passwordStrength.weak') : score <= 3 ? t('passwordStrength.medium') : score <= 4 ? t('passwordStrength.good') : t('passwordStrength.excellent');
  const strengthColor =
    score <= 1
      ? 'bg-destructive'
      : score <= 3
        ? 'bg-yellow-500'
        : score <= 4
          ? 'bg-blue-500'
          : 'bg-green-500';

  if (!password) return null;

  return (
    <div className="space-y-2 text-xs">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden flex gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className={`flex-1 rounded-full transition-colors duration-300 ${i < score ? strengthColor : 'bg-muted'}`}
            />
          ))}
        </div>
        <span className="text-muted-foreground font-medium min-w-[60px] text-right">{strengthLabel}</span>
      </div>

      {/* Rules checklist */}
      <ul className="space-y-0.5">
        {results.map((r) => (
          <li key={r.key} className={`flex items-center gap-1.5 ${r.pass ? 'text-green-600' : 'text-muted-foreground'}`}>
            {r.pass ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
            {t(`passwordStrength.${r.key}`)}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PasswordStrengthIndicator;
